import type { RenderingAmbiance, RenderingOptions, RenderingTint } from "../types";

export const DEFAULT_RENDERING_OPTIONS: Required<RenderingOptions> = {
  ambiance: "soft",
  contactShadows: true,
  contactShadowStrength: 1,
  decorShadows: true,
  sunAzimuth: 315,
  tint: "neutral",
  tintStrength: 1,
  arrivalFlight: false,
  spotlight: true,
  lightIntensity: 0.24,
  lightAngle: 28,
};

type AmbianceSettings = Pick<Required<RenderingOptions>, "lightAngle" | "lightIntensity" | "contactShadowStrength" | "tint"> & {
  /** D'où vient le soleil, par rapport au haut de l'écran dans la vue de départ : -45 le place en haut à gauche. */
  sunOffset: number;
};

export const RENDERING_AMBIANCES: Record<Exclude<RenderingAmbiance, "custom">, AmbianceSettings> = {
  soft: { sunOffset: -45, lightAngle: 30, lightIntensity: 0.2, contactShadowStrength: 0.8, tint: "neutral" },
  noon: { sunOffset: -45, lightAngle: 12, lightIntensity: 0.24, contactShadowStrength: 1, tint: "cool" },
  evening: { sunOffset: -75, lightAngle: 48, lightIntensity: 0.3, contactShadowStrength: 0.7, tint: "warm" },
  night: { sunOffset: -45, lightAngle: 35, lightIntensity: 0.3, contactShadowStrength: 1.6, tint: "night" },
  contrast: { sunOffset: -45, lightAngle: 42, lightIntensity: 0.42, contactShadowStrength: 1.4, tint: "neutral" },
};

interface TintPalette {
  /** Lumière des volumes. */
  light: string;
  /** Lumière directionnelle des objets 3D. */
  decorSun: string;
  /** Lumière d'ambiance des objets 3D : venue du ciel, et renvoyée par le sol. */
  decorSky: string;
  decorGround: string;
  shadow: string;
  /** Voile posé sur la carte, du haut vers le bas de l'écran, en multiplication : `[r, g, b, alpha]`. */
  veilTop: [number, number, number, number];
  veilBottom: [number, number, number, number];
}

export const RENDERING_TINTS: Record<RenderingTint, TintPalette> = {
  neutral: { light: "#ffffff", decorSun: "#ffffff", decorSky: "#ffffff", decorGround: "#8a8f80", shadow: "#1d1d1b", veilTop: [255, 255, 255, 0], veilBottom: [255, 255, 255, 0] },
  cool: { light: "#f4f8ff", decorSun: "#f6f9ff", decorSky: "#f2f7ff", decorGround: "#8a909a", shadow: "#1b2230", veilTop: [190, 215, 255, 0.07], veilBottom: [210, 225, 255, 0.03] },
  warm: { light: "#ffe3c8", decorSun: "#ffd9b0", decorSky: "#ffe9d6", decorGround: "#8a7a90", shadow: "#3b2a55", veilTop: [255, 150, 70, 0.13], veilBottom: [255, 180, 110, 0.06] },
  night: { light: "#d2d9ee", decorSun: "#c4cdec", decorSky: "#aab6d8", decorGround: "#3a3f5a", shadow: "#0d1430", veilTop: [45, 60, 120, 0.3], veilBottom: [35, 45, 95, 0.18] },
};

function mixHex(from: string, to: string, amount: number): string {
  const parse = (hex: string) => [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16));
  const a = parse(from);
  const b = parse(to);
  return `#${a.map((value, index) => Math.round(Math.min(255, Math.max(0, value + (b[index]! - value) * amount))).toString(16).padStart(2, "0")).join("")}`;
}

/** Les couleurs de la teinte, dosées : à 0 celles du neutre, à 2 deux fois plus éloignées de lui. */
export function resolveTint(tint: RenderingTint, strength: number): Omit<TintPalette, "veilTop" | "veilBottom"> & { veil: string | null } {
  const neutral = RENDERING_TINTS.neutral;
  const palette = RENDERING_TINTS[tint] ?? neutral;
  const veil = (color: [number, number, number, number]) => `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${Math.min(1, color[3] * strength)})`;
  const hasVeil = palette.veilTop[3] > 0 && strength > 0;
  return {
    light: mixHex(neutral.light, palette.light, strength),
    decorSun: mixHex(neutral.decorSun, palette.decorSun, strength),
    decorSky: mixHex(neutral.decorSky, palette.decorSky, strength),
    decorGround: mixHex(neutral.decorGround, palette.decorGround, strength),
    shadow: mixHex(neutral.shadow, palette.shadow, strength),
    veil: hasVeil ? `linear-gradient(180deg, ${veil(palette.veilTop)}, ${veil(palette.veilBottom)})` : null,
  };
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Chaque réglage effectif, défaut compris, ramené dans ses bornes. Une ambiance place le soleil par
 * rapport à `viewBearingDeg`, le cap de la vue de départ.
 */
export function resolveRenderingOptions(options: RenderingOptions | undefined, viewBearingDeg = 0): Required<RenderingOptions> {
  const resolved = { ...DEFAULT_RENDERING_OPTIONS, ...options };
  if (resolved.ambiance !== "custom") {
    const { sunOffset, ...settings } = RENDERING_AMBIANCES[resolved.ambiance] ?? RENDERING_AMBIANCES.soft;
    Object.assign(resolved, settings, { sunAzimuth: viewBearingDeg + sunOffset });
  }
  return {
    ...resolved,
    contactShadowStrength: clamp(resolved.contactShadowStrength, 0, 2),
    tintStrength: clamp(resolved.tintStrength, 0, 2),
    sunAzimuth: ((resolved.sunAzimuth % 360) + 360) % 360,
    lightIntensity: clamp(resolved.lightIntensity, 0, 1),
    lightAngle: clamp(resolved.lightAngle, 0, 80),
  };
}
